import React from 'react'
import ReactHighcharts from 'react-highcharts'
import { Col, Table } from 'react-bootstrap'
import numeral from 'numeral'
import moment from 'momentExt'
import { hcFontFamily, hcDefaultColors } from 'modules/chart'
import { localStoragePayhelm } from 'modules/utils'
import { toggleBrandFacetFilter, toggleFacetFilterValue, formatters as formattingFormatters } from 'modules/formatting'
import ReactTooltip from 'react-tooltip'
import { ChartSkeleton } from 'sharedComponents/Skeleton'
import { ChevronDown, Check } from 'lucide-react'

const EmptyChart = ({title, height}: {title?: string, height?: number}) => (
  <div className="empty-chart-message" style={height ? { minHeight: height } : undefined}>
    <div className="title">{title}</div>
    <div className="message">No data to display</div>
  </div>
)

export default class CustomHighcharts extends React.Component {
  state = {
    metricDropdownOpen: false,
    metricSearch: '',
  }

  private metricDropdownRef = React.createRef<HTMLDivElement>()

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (
      this.state.metricDropdownOpen &&
      this.metricDropdownRef.current &&
      !this.metricDropdownRef.current.contains(e.target as Node)
    ) {
      this.setState({ metricDropdownOpen: false, metricSearch: '' })
    }
  }

  /**
   * Set chart type of highcharts
   * @param {String} chartType 'table', 'column' or 'pie'
   */
  setChartType = (chartType) => {
    const { facetName, setChartTypeCallback } = this.props
    if (facetName && (chartType !== this.props.chartType)) {
      localStoragePayhelm[`${facetName}_chart`] = chartType
      setChartTypeCallback(facetName, chartType)
    }
  }

  convertToSecondaryConfigs = () => {
    const {
      facet, prevFacet, prevRange, currentRange
    } = this.props
    const facetSeries = (facet.series && facet.series[facet.metric]) || []
    const facetFormatters = (facet.formatters && facet.formatters[facet.metric]) || {}
    const visibleCategories = (facet.categories || []).map((bucket, index) => bucket !== 'N/A' || facetSeries[index])
    const visibleFilter = (bucket, index) => visibleCategories[index]
    const categories = (facet.categories || []).filter(visibleFilter)
    const values = (facet.values || []).filter(visibleFilter)
    const brandSources = (facet.brandSources || []).filter(visibleFilter)
    const merchantIds = (facet.merchantIds || []).filter(visibleFilter)
    const series = facetSeries.filter(visibleFilter)
    const prevSeriesRaw = prevFacet && prevFacet.series && prevFacet.series[prevFacet.metric] && Array.isArray(prevFacet.series[prevFacet.metric])
      ? prevFacet.series[prevFacet.metric]
      : null
    let prevSeries = prevSeriesRaw ? prevSeriesRaw.filter(visibleFilter) : null
    if (prevSeries && prevSeries.length < series.length) {
      prevSeries = [...prevSeries, ...Array(series.length - prevSeries.length).fill(null)]
    }
    let currentRangeLabel = currentRange ? `${moment(currentRange.startDate).format('MMM DD')} - ${moment(currentRange.endDate).format('MMM DD')}` : ''
    let prevRangeLabel = prevFacet ? `${moment(prevRange.startDate).format('MMM DD')} - ${moment(prevRange.endDate).format('MMM DD')}` : ''
    if (currentRangeLabel === prevRangeLabel) {
      currentRangeLabel = currentRange ? `${moment(currentRange.startDate).format('MMM DD YYYY')} - ${moment(currentRange.endDate).format('MMM DD YYYY')}` : ''
      prevRangeLabel = prevRange ? `${moment(prevRange.startDate).format('MMM DD YYYY')} - ${moment(prevRange.endDate).format('MMM DD YYYY')}` : ''
    }
    return {
      facetFormatters,
      categories,
      values,
      brandSources,
      merchantIds,
      series,
      prevSeries,
      currentRangeLabel,
      prevRangeLabel,
    }
  }

  isBrandChart = () => this.props.facet?.key === 'brand_ids'

  getBrandEntryAtIndex = (index) => {
    const { values, brandSources, merchantIds } = this.convertToSecondaryConfigs()
    return {
      val: values[index],
      _brandSource: brandSources[index] || 'brand_ids',
      merchant_id: merchantIds[index],
    }
  }

  asFilterArray = (v) => (Array.isArray(v) ? v : [null])

  isBucketFilterActive = (index) => {
    const { facet, currentFilter, brandNamesFilter } = this.props
    const { categories, values, brandSources } = this.convertToSecondaryConfigs()
    if (this.isBrandChart()) {
      const entryKey = values[index]
      const brandSource = brandSources[index] || 'brand_ids'
      const activeFilter = brandSource === 'brand_names'
        ? this.asFilterArray(brandNamesFilter)
        : this.asFilterArray(currentFilter)
      return activeFilter.includes(entryKey)
    }
    const bucket = categories[index]
    const entryKey = facet.reversedDict ? facet.reversedDict[bucket] : bucket
    return this.asFilterArray(currentFilter).includes(entryKey)
  }

  handleBrandFilterClickByIndex = (index) => {
    const { currentFilter, brandNamesFilter } = this.props
    const brandUpdate = toggleBrandFacetFilter(
      this.getBrandEntryAtIndex(index),
      this.asFilterArray(currentFilter),
      this.asFilterArray(brandNamesFilter),
    )
    if (!brandUpdate) {
      return
    }
    this.handleFilterClick(brandUpdate)
  }

  handleFacetFilterClickByIndex = (index) => {
    if (this.isBrandChart()) {
      this.handleBrandFilterClickByIndex(index)
      return
    }
    const { facet, currentFilter } = this.props
    const { categories, values } = this.convertToSecondaryConfigs()
    const bucket = categories[index]
    const entryKey = facet.reversedDict ? facet.reversedDict[bucket] : values[index] ?? bucket
    const activeFilter = this.asFilterArray(currentFilter)
    const nextFilter = toggleFacetFilterValue(entryKey, activeFilter)
    if (nextFilter === null) {
      return
    }
    this.handleFilterClick(facet.key, nextFilter)
  }

  handleFilterClick = (filterKeyOrUpdate, nextFilter?) => {
    const { filterable, updateFilter, toggleFilter, filterVisible, setForceSearch, setFilterUpdated } = this.props
    if (!filterable) {
      return
    }
    const filterUpdate = (
      typeof filterKeyOrUpdate === 'object' && filterKeyOrUpdate !== null && !Array.isArray(filterKeyOrUpdate)
    ) ? filterKeyOrUpdate : { [filterKeyOrUpdate]: nextFilter }
    if (updateFilter) {
      updateFilter(filterUpdate)
      if (toggleFilter && filterVisible !== true) {
        toggleFilter()
      }
    }
    if (setForceSearch && !filterVisible) {
      setForceSearch(true, true)
    }
    if (setFilterUpdated) {
      setFilterUpdated(true)
    }
  }

  getTable = () => {
    const { facet, height, prevFacet, filterable, limitedAccess, checkAccess } = this.props
    const { xFormatter, yAxisTitle, yPrefix } = facet
    const { facetFormatters, categories, series, prevSeries, currentRangeLabel, prevRangeLabel } = this.convertToSecondaryConfigs()
    return (
      <div style={{ padding: 10, height }}>
        <div className="chart-facet-title">{facet.title}</div>
        <div style={{ overflow: 'auto', height: height - 50, borderRadius: 6 }}>
          <Table hover responsive className="list-table">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>{yAxisTitle}{prevFacet ? <span><br/>{` (${currentRangeLabel})`}</span> : null}</th>
                {
                  prevFacet ? (
                    <th>{yAxisTitle}{prevFacet ? <span><br/>{` (${prevRangeLabel})`}</span> : null}</th>
                  ) : null
                }
              </tr>
            </thead>
            <tbody>
              {
                categories.map((bucket, index) => {
                  const filterInEffect = this.isBucketFilterActive(index)
                  return (
                    <tr key={index}>
                      <td>
                        {
                          filterable ? (
                            <a
                              className="cursor-pointer"
                              title={filterInEffect ? 'Remove filter' : 'Add filter'}
                              onClick={limitedAccess ? checkAccess : () => this.handleFacetFilterClickByIndex(index)}
                            >
                              {xFormatter ? xFormatter(bucket) : bucket}&nbsp;
                              <span
                                className="fa fa-filter"
                                style={filterInEffect ? { textDecoration: 'line-through'} : {}}
                              />
                            </a>
                          ) : (xFormatter ? xFormatter(bucket) : bucket)
                        }
                      </td>
                      <td>{`${yPrefix || ''} ${facetFormatters.formatter ? facetFormatters.formatter(series[index]) : numeral(series[index]).format(facetFormatters.format)}`}</td>
                      {
                        prevFacet ? (
                          <td>
                            {
                              prevSeries[index] === null ?
                              <span className="link-style" data-tip data-for={`chart-na-tooltip-${facet.key}-${index}`}>N/A</span> :
                              `${yPrefix || ''} ${facetFormatters.formatter ? facetFormatters.formatter(prevSeries[index]) : numeral(prevSeries[index]).format(facetFormatters.format)}`
                            }
                            <ReactTooltip id={`chart-na-tooltip-${facet.key}-${index}`}>
                              <div style={{ maxWidth: 200 }}>
                                This item has no sales or is not in the top search results during the compared period.
                              </div>
                            </ReactTooltip>
                          </td>
                        ) : null
                      }
                    </tr>
                  )
                })
              }
            </tbody>
          </Table>
        </div>
      </div>
    )
  }

  /**
   * Convert facet data and other props into Highcharts' config
   */
  getChartConfig = chartType => {
    const { handleFacetFilterClickByIndex } = this
    const { facet, height, legend, prevFacet, filterable, limitedAccess, checkAccess } = this.props
    const { xFormatter, yAxisTitle, yPrefix, ySuffix } = facet
    const { facetFormatters, categories, values, series, prevSeries, currentRangeLabel, prevRangeLabel } = this.convertToSecondaryConfigs()
    const isBrandChart = facet.key === 'brand_ids'
    return {
      chart: {
        type: chartType,
        height: height || (prevFacet ? 250 : 200),
        style: {
          fontFamily: hcFontFamily,
        }
      },
      colors: hcDefaultColors,
      legend: {
        enabled: !!prevFacet || legend,
        labelFormatter: function () {
          return this.index ? prevRangeLabel : currentRangeLabel
        }
      },
      credits: {
        enabled: false,
      },
      title: {
        text: facet.title,
        align: 'left',
        style: {
          fontSize: '12px',
          fontWeight: '500',
          color: '#6b7280',
        },
      },
      plotOptions: {
        pie: {
          size: 160,
          dataLabels: {
            style: { textTransform: 'capitalize' },
          }
        },
        series: (facet.key === 'hour_sold' || !facet.key) ? {} : {
          cursor: filterable ? 'pointer' : 'default',
          point: {
            events: {
              click: limitedAccess ? checkAccess : function() {
                handleFacetFilterClickByIndex(this.index)
              }
            }
          }
        }
      },
      xAxis: {
        labels: {
          style: { textTransform: 'capitalize' },
          ...(xFormatter ? {
            formatter: function() {
              return xFormatter(this.value)
            }
          } : {})
        },
        ...(chartType === 'column' ? { categories: (categories || []).map(c => c ?? '') } : {}),
      },
      yAxis: chartType === 'column' ? {
        title: { text: yAxisTitle },
        labels: {
          formatter: function() {
            return `${numeral(this.value).format(Math.abs(this.value) >= 10000 ? facetFormatters.axis_format_big : facetFormatters.axis_format)}`
          }
        },
        minRange: 1,
        minTickInterval: 1,
      } : {},
      tooltip: {
        formatter: function() {
          let dateLabel = prevFacet ? `${this.series.index ? prevRangeLabel : currentRangeLabel}<br/>` : ''
          if (this.y === null) {
            return dateLabel + `N/A (This item has no sales or is not in the top search results during the compared period.)`
          }
          return dateLabel + `${yPrefix || ''} ${(
            chartType === 'column' && xFormatter
          ) ? xFormatter(this.key) : this.key}: ${
            facetFormatters.formatter ? facetFormatters.formatter(this.y) : numeral(this.y).format(facetFormatters.format)} ${ySuffix || ''
          }`
        },
        style: { textTransform: 'capitalize' },
      },
      series: chartType === 'column' ? [series, prevSeries].filter(s => s).map(s => ({
        name: yAxisTitle,
        data: (s || []).map((v, index) => {
          if (v === undefined) return null
          if (isBrandChart) {
            return { val: values[index], y: v }
          }
          return v === null ? null : v
        }),
      })) : [{
        name: yAxisTitle,
        data: categories.map((bucket, index) => {
          const yVal = series[index]
          if (yVal === undefined) return null
          return { val: values[index], name: xFormatter ? xFormatter(bucket) : bucket, y: yVal }
        }).filter(Boolean),
      }]
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.state.metricDropdownOpen !== nextState.metricDropdownOpen ||
        this.state.metricSearch !== nextState.metricSearch) {
      return true
    }
    // currentFilter is normally a value array; name-value facets (metafields) store nested
    // objects on filters[facetKey], so coerce non-arrays before comparing.
    const filtersEqual = (a, b) => {
      const left = this.asFilterArray(a)
      const right = this.asFilterArray(b)
      return left.length === right.length && left.every((val, index) => val === right[index])
    }
    let filterUpdated = false
    if (!nextProps.fetching && !this.props.fetching) {
      filterUpdated = !filtersEqual(this.props.currentFilter, nextProps.currentFilter)
        || !filtersEqual(this.props.brandNamesFilter, nextProps.brandNamesFilter)
        || (this.props.facet.chart_type !== nextProps.facet.chart_type)
    }
    return filterUpdated || (
      this.props.fetching && !nextProps.fetching
    ) || (
      this.props.facet !== nextProps.facet
    ) || (
      this.props.facetLoading !== nextProps.facetLoading
    ) || (
      this.props.currentMetric !== nextProps.currentMetric
    )
  }

  renderMetricDropdown = (currentMetric: string) => {
    const { onMetricChange, availableMetrics } = this.props
    if (!onMetricChange) return null

    const { metricDropdownOpen, metricSearch } = this.state
    const metrics = (availableMetrics || [])
      .filter((m: string) => formattingFormatters.stats[m])
      .sort((a: string, b: string) =>
        (formattingFormatters.stats[a]?.title || '').localeCompare(formattingFormatters.stats[b]?.title || '')
      )

    const filtered = metricSearch
      ? metrics.filter((m: string) =>
          (formattingFormatters.stats[m]?.title || '').toLowerCase().includes(metricSearch.toLowerCase())
        )
      : metrics

    const currentTitle = formattingFormatters.stats[currentMetric]?.title || currentMetric

    return (
      <div className="tw-relative tw-inline-flex" ref={this.metricDropdownRef}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            this.setState({ metricDropdownOpen: !metricDropdownOpen, metricSearch: '' })
          }}
          className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-gray-500 hover:tw-text-blue-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-whitespace-nowrap"
        >
          {currentTitle}
          <ChevronDown size={12} />
        </button>
        {metricDropdownOpen && (
          <div className="tw-absolute tw-top-full tw-right-0 tw-mt-1 tw-w-56 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-z-50 tw-overflow-hidden">
            <div className="tw-p-2 tw-border-b tw-border-gray-100">
              <input
                type="text"
                placeholder="Search metrics..."
                value={metricSearch}
                onChange={(e) => this.setState({ metricSearch: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                className="tw-w-full tw-px-2 tw-py-1 tw-text-xs tw-border tw-border-gray-200 tw-rounded tw-outline-none focus:tw-border-blue-400"
              />
            </div>
            <div className="tw-max-h-48 tw-overflow-y-auto tw-py-1">
              {filtered.length > 0 ? (
                filtered.map((m: string) => {
                  const isActive = m === currentMetric
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onMetricChange(m)
                        this.setState({ metricDropdownOpen: false, metricSearch: '' })
                      }}
                      className={`tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-xs tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-between ${
                        isActive
                          ? 'tw-bg-blue-50 tw-text-blue-700 tw-font-medium'
                          : 'tw-bg-transparent tw-text-gray-700 hover:tw-bg-gray-50'
                      }`}
                    >
                      {formattingFormatters.stats[m].title}
                      {isActive && <Check size={12} className="tw-text-blue-600" />}
                    </button>
                  )
                })
              ) : (
                <div className="tw-px-3 tw-py-2 tw-text-xs tw-text-gray-400">No metrics found</div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  render() {
    const { cols, facet, facetName, empty, chartTypes = ['column', 'pie', 'table'], style, className = '', facetLoading, height, onMetricChange } = this.props

    // Show skeleton when this specific facet is loading
    if (facetLoading) {
      return (
        <Col xs={cols.xs} sm={cols.sm} md={cols.md} lg={cols.lg}>
          <div style={{position: "relative", ...style}} className={`custom-chart-box ${className}`}>
            <ChartSkeleton height={height || 300} />
          </div>
        </Col>
      )
    }

    if (!facet || !facet.series || facet.series[facet.metric] == null) {
      return (
        <Col xs={cols.xs} sm={cols.sm} md={cols.md} lg={cols.lg}>
          <div style={{position: "relative", ...style}} className={`custom-chart-box ${className}`}>
            <EmptyChart title={facet?.title} height={height} />
          </div>
        </Col>
      )
    }

    const facetSeries = Array.isArray(facet.series[facet.metric]) ? facet.series[facet.metric] : []
    const chartType = this.props.chartType || (facetName ? (facet.chart_type || localStoragePayhelm[`${facetName}_chart`] || facet.default_chart || 'column') : 'column')

    let body
    if (!facetSeries.length || empty) {
      body = <EmptyChart title={facet?.title} height={height} />
    }
    else if (chartType === 'table') {
      body = this.getTable()
    }
    else {
      body = <ReactHighcharts config={this.getChartConfig(chartType)} />
    }

    return (
      <Col xs={cols.xs} sm={cols.sm} md={cols.md} lg={cols.lg}>
        <div style={{position: "relative", ...style}} className={`custom-chart-box ${className}`}>
          {
            (chartTypes.length > 1 || onMetricChange) ? (
              <div className="custom-chart-type-menu">
                {this.renderMetricDropdown(facet.metric)}
                {chartTypes.length > 1 && onMetricChange && (
                  <span className="tw-mx-1 tw-text-gray-300">|</span>
                )}
                {
                  chartTypes.includes('column') ? (
                    <span
                      className={`fa fa-bar-chart ${chartType == 'column' ? 'active' : ''}`}
                      onClick={() => chartType === 'column' ? null : this.setChartType('column')}
                    />
                  ) : null
                }
                {
                  chartTypes.includes('pie') ? (
                    <span
                      className={`fa fa-pie-chart ${chartType == 'pie' ? 'active' : ''}`}
                      onClick={() => chartType === 'pie' ? null : this.setChartType('pie')}
                    />
                  ) : null
                }
                {
                  chartTypes.includes('table') ? (
                    <span
                      className={`fa fa-table ${chartType == 'table' ? 'active' : ''}`}
                      onClick={() => chartType === 'table' ? null : this.setChartType('table')}
                    />
                  ) : null
                }
              </div>
            ) : null
          }
          { body }
        </div>
      </Col>
    )
  }
}
